import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { customerActions } from "_store/actions";

import { ReactComponent as CardVISASVG } from "assets/img/CardVISA.svg";
import { ReactComponent as CardAMEXSVG } from "assets/img/CardAMEX.svg";
import { ReactComponent as CardDISCOVERSVG } from "assets/img/CardDISCOVER.svg";
import { ReactComponent as CardMASTERCARDSVG } from "assets/img/CardMASTERCARD.svg";
import { ReactComponent as CardTESTCARDSVG } from "assets/img/CardTESTCARD.svg";

import AddPaymentMethodModal from "./AddPaymentMethodModal";

import breakpoints from "_constants/breakpoints";
import { SpinnerWrapper as Spinner } from "widget/Spinner";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  background-color: #ffffff;
  width: 100%;
  max-width: 660px;
  padding: 24px;
  margin-bottom: 32px;

  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }

  ${({ theme: { mobile } }) =>
    !mobile &&
    css`
      box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
    `}

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 24px 16px;
  }
`;
const Title = styled.div`
  height: 81px;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 47px;
  text-align: left;
  color: #03589c;
  padding: 5px 0 29px 8px;
  border-bottom: 1px solid #ecedf1;
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      font-size: 22px;
      text-align: center;
      border-bottom: none;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 18px;
        line-height: 24px;
        padding: 0px;
        height: 64px;
      }
    `}
`;
const InfoForm = styled.div`
  padding: 48px;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      padding: 0 0 24px;
    `}
`;
const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
  color: #7b848d;
  
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      padding: 10px 0;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 14px;
      }
    `}
  &:not(:last-child) {
    margin-bottom: 14px;
  }
`;
const CardIcon = styled.div`
  width: 35px;
  margin: 0 8px;

  @media (max-width: ${breakpoints.mobileM}) {
    margin: 0px 0px;
  }
`;
const CardInfo = styled.span`
  font-weight: 700;
`;
const AddPaymentButton = styled.button`
  align-self: center;
  width: 226px;
  color: #ffffff;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-weight: 700;

  @media (max-width: ${breakpoints.mobileM}) {
    width: 185px;
  }
`;
const Indicator = styled.div`
  min-width: 10px;
  max-width: 10px;
  min-height: 10px;
  max-height: 10px;
  border-radius: 50%;
  background-color: var(--bs-teal);

  ${({ primary }) =>
    css`
      opacity: ${primary ? "100%" : 0};
    `}
`;
const cardIcon = {
  CardVISASVG,
  CardAMEXSVG,
  CardDISCOVERSVG,
  CardMASTERCARDSVG,
};

function PaymentMethod({ cardNumber, cardType = "", cardMonth, cardYear, primary }) {
  const CardIconSVG = cardIcon[`Card${cardType.toUpperCase()}SVG`];

  return (
    <CardRow>
      Card:
      <CardIcon>{CardIconSVG ? <CardIconSVG /> : <CardTESTCARDSVG />}</CardIcon>
      <CardInfo>
        Ending in {cardNumber} expires {cardMonth}/{cardYear}
      </CardInfo>
      <Indicator title="Primary Card" primary={primary} />
    </CardRow>
  );
}

PaymentMethod.propTypes = {
  cardNumber: PropTypes.string,
  cardType: PropTypes.string,
  cardMonth: PropTypes.string,
  cardYear: PropTypes.string,
  primary: PropTypes.bool,
};

export default function PaymentMethods() {
  const dispatch = useDispatch();

  const { paymentMethods, paymentMethodsLoading } = useSelector(({ customer }) => customer);
  const primaryCard = paymentMethods?.find(({ primary }) => primary);

  const [isModalOpen, setModalOpen] = useState(false);

  const handleAddPaymentMethod = (cardDetails) => {
    setModalOpen(false);
    dispatch(customerActions.postPaymentMethod(cardDetails));
  };

  useEffect(() => {
    dispatch(customerActions.getPaymentMethods());
  }, [dispatch]);

  return (
    <>
      {paymentMethodsLoading ? (
        <Spinner />
      ) : (
        <Container>
          <Title>Payment Methods</Title>
          <InfoForm>
            {paymentMethods.map((cardInfo, idx) => (
              <PaymentMethod key={idx} {...cardInfo} />
            ))}
          </InfoForm>
          <AddPaymentButton onClick={() => setModalOpen(true)}>
            Add/Replace
            <br />
            Payment Method
          </AddPaymentButton>
          <AddPaymentMethodModal
            show={isModalOpen}
            onCancel={() => setModalOpen(false)}
            onConfirm={handleAddPaymentMethod}
            primaryId={primaryCard?.paySourceId}
          />
        </Container>
      )}
    </>
  );
}
